<script setup>
/* eslint-disable vue/no-bare-strings-in-template */
import { computed, onMounted, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import OlistAPI from 'dashboard/api/integrations/olist';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

const olistLogoUrl = '/dashboard/images/integrations/olist.png';
const isLoading = ref(false);
const isSaving = ref(false);
const isSyncing = ref(false);
const status = ref({});
const form = ref({
  integration_name: 'Joota',
  integrator_id: '',
  api_token: '',
  freight_quote_url: '',
});

const isConnected = computed(() => status.value.connected);
const webhookEntries = computed(() =>
  Object.entries(
    status.value.erp_webhook_urls || status.value.webhook_urls || {}
  ).map(([key, value]) => ({
    key,
    value,
    label:
      {
        products: 'Produtos',
        stock: 'Estoque',
        prices: 'Preços',
        order_status: 'Situação de pedidos',
        tracking: 'Rastreio',
        invoice: 'Nota fiscal',
      }[key] || key,
  }))
);

const loadStatus = async () => {
  isLoading.value = true;
  try {
    const { data } = await OlistAPI.status();
    status.value = data || {};
    form.value.integration_name = data.integration_name || 'Joota';
    form.value.integrator_id = data.integrator_id || '';
  } catch {
    useAlert('Não foi possível carregar a integração Olist');
  } finally {
    isLoading.value = false;
  }
};

const saveIntegration = async () => {
  if (!form.value.api_token.trim()) {
    useAlert('Informe o token da API da Olist');
    return;
  }

  isSaving.value = true;
  try {
    const { data } = await OlistAPI.update(form.value);
    status.value = data || {};
    form.value.api_token = '';
    useAlert('Integração Olist conectada');
  } catch (error) {
    useAlert(
      error?.response?.data?.error || 'Não foi possível conectar a Olist'
    );
  } finally {
    isSaving.value = false;
  }
};

const syncProducts = async () => {
  isSyncing.value = true;
  try {
    const { data } = await OlistAPI.syncProducts();
    status.value = data || status.value;
    useAlert(
      data.message ||
        `${data.synced_count || 0} produtos recebidos pelos webhooks`
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error || 'Não foi possível sincronizar produtos'
    );
  } finally {
    isSyncing.value = false;
  }
};

const copyWebhook = async value => {
  await copyTextToClipboard(value);
  useAlert('URL copiada');
};

onMounted(loadStatus);
</script>

<template>
  <div class="mx-auto flex max-w-4xl flex-col gap-6 p-4">
    <div
      v-if="isLoading"
      class="flex min-h-[20rem] items-center justify-center"
    >
      <Spinner size="" color-scheme="primary" />
    </div>

    <template v-else>
      <section
        class="overflow-hidden rounded-2xl border border-n-weak bg-white shadow-sm dark:bg-n-solid-2"
      >
        <div
          class="flex flex-col items-center gap-3 border-b border-n-weak px-6 py-8 text-center"
        >
          <img
            :src="olistLogoUrl"
            alt="Olist"
            class="h-16 w-16 rounded-2xl object-contain"
          />
          <div>
            <h2 class="text-2xl font-semibold text-n-slate-12">Olist ERP</h2>
            <p class="mt-1 max-w-xl text-sm leading-6 text-n-slate-11">
              Conecte a integração personalizada API do ERP para receber
              produtos, estoque, preços e atualizações de pedidos no Joota.
            </p>
          </div>
          <span
            class="rounded-full px-3 py-1 text-xs font-semibold"
            :class="
              isConnected
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-slate-3 text-n-slate-11'
            "
          >
            {{ isConnected ? 'Conectado' : 'Desconectado' }}
          </span>
        </div>

        <div class="grid gap-4 px-6 py-6 md:grid-cols-2">
          <label
            class="flex flex-col gap-2 text-sm font-medium text-n-slate-12"
          >
            Nome da integração
            <input
              v-model="form.integration_name"
              class="rounded-xl border border-n-weak bg-n-alpha-1 px-4 py-3 text-sm outline-none transition focus:border-n-brand focus:bg-white dark:bg-n-solid-3"
              placeholder="Joota"
            />
          </label>

          <label
            class="flex flex-col gap-2 text-sm font-medium text-n-slate-12"
          >
            Identificador do integrador
            <input
              v-model="form.integrator_id"
              class="rounded-xl border border-n-weak bg-n-alpha-1 px-4 py-3 text-sm outline-none transition focus:border-n-brand focus:bg-white dark:bg-n-solid-3"
              placeholder="40479"
            />
          </label>

          <label
            class="flex flex-col gap-2 text-sm font-medium text-n-slate-12 md:col-span-2"
          >
            Token da integração API do ERP
            <input
              v-model="form.api_token"
              type="password"
              class="rounded-xl border border-n-weak bg-n-alpha-1 px-4 py-3 text-sm outline-none transition focus:border-n-brand focus:bg-white dark:bg-n-solid-3"
              :placeholder="
                isConnected
                  ? 'Cole um novo token somente se quiser substituir'
                  : 'Cole o token da integração personalizada API do ERP'
              "
            />
            <span class="text-xs font-normal leading-5 text-n-slate-10">
              Este não é o token OAuth/API v3. Use o token da integração
              personalizada API do ERP. Ele fica salvo somente no backend.
            </span>
          </label>

          <label
            class="flex flex-col gap-2 text-sm font-medium text-n-slate-12 md:col-span-2"
          >
            Endpoint para cotação de fretes da Olist
            <input
              v-model="form.freight_quote_url"
              class="rounded-xl border border-n-weak bg-n-alpha-1 px-4 py-3 text-sm outline-none transition focus:border-n-brand focus:bg-white dark:bg-n-solid-3"
              placeholder="https://erp.olist.com/webhook/api/v1/parceiro/40479/cotar"
            />
          </label>
        </div>

        <div
          class="flex flex-col gap-3 border-t border-n-weak px-6 py-5 sm:flex-row sm:items-center sm:justify-between"
        >
          <div class="text-sm text-n-slate-11">
            {{ status.synced_products_count || 0 }} produtos recebidos da Olist
            no catálogo. Limite: {{ status.product_limit || 1000 }} por conta.
          </div>
          <div class="flex gap-2">
            <Button
              faded
              blue
              :is-loading="isSaving"
              :label="isConnected ? 'Atualizar conexão' : 'Conectar'"
              @click="saveIntegration"
            />
            <Button
              v-if="isConnected"
              faded
              slate
              :is-loading="isSyncing"
              label="Verificar produtos"
              @click="syncProducts"
            />
          </div>
        </div>
      </section>

      <section
        v-if="isConnected"
        class="rounded-2xl border border-n-weak bg-white p-6 shadow-sm dark:bg-n-solid-2"
      >
        <div class="mb-4">
          <h3 class="text-lg font-semibold text-n-slate-12">
            URLs para configurar na Olist
          </h3>
          <p class="mt-1 text-sm leading-6 text-n-slate-11">
            Copie cada URL para o respectivo campo de notificações da integração
            personalizada API do ERP. Os produtos chegam por essas URLs.
          </p>
        </div>

        <div class="grid gap-3">
          <div
            v-for="entry in webhookEntries"
            :key="entry.key"
            class="flex flex-col gap-2 rounded-xl border border-n-weak bg-n-alpha-1 p-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <div class="min-w-0">
              <p class="text-sm font-medium text-n-slate-12">
                {{ entry.label }}
              </p>
              <p class="truncate text-xs text-n-slate-10">{{ entry.value }}</p>
            </div>
            <Button
              faded
              slate
              xs
              label="Copiar"
              @click="copyWebhook(entry.value)"
            />
          </div>
        </div>
      </section>
    </template>
  </div>
</template>
